import { useRef } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Request } from './sources';
import type { DataSource, ShowFn, ShowResult, UseSingleConfig } from './types';

export default function useSingle<I extends string, F extends ShowFn<I>>(
  config: UseSingleConfig<I, F>
): DataSource<ShowResult<F>> {
  const queryClient = useQueryClient();
  const isEditing = !!config.id;

  const { data, isLoading } = useQuery({
    queryKey: isEditing ? config.queryKey.show(config.id!) : ['__datasource_noop__'],
    queryFn: ({ signal }) => config.show(config.id!, signal as AbortSignal, config.include),
    enabled: isEditing,
  });

  const requestRef = useRef<Request<ShowResult<F>>>(null!);
  if (requestRef.current === null) {
    requestRef.current = new Request<ShowResult<F>>();
  }

  requestRef.current._syncState({
    queryData: isEditing ? data : undefined,
    loading: isEditing && isLoading,
    mode: isEditing ? 'put' : 'post',
    id: config.id,
    create: config.create,
    update: config.update,
    onSaveSuccess: () => {
      const indexKey = typeof config.queryKey.index === 'function' ? config.queryKey.index() : config.queryKey.index;
      queryClient.invalidateQueries({ queryKey: indexKey });
      if (config.id) {
        queryClient.removeQueries({ queryKey: config.queryKey.show(config.id) });
      }
    },
  });

  return requestRef.current;
}
